import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler, Renderer} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Toggle} from '../../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {FolderBreadcrumb} from '../../../components/folderBreadcrumbs/folderBreadcrumbs';
import {computeIsGlobalNamespace, isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {SettingsBreadcrumbs} from '../../common/settingsBreadcrumbs';
import {SettingsHeader, SettingsHeaderProps} from '../../settingsHeader';
import {RuleRecipe} from '../library/recipes';
import {EditorStateActions} from './hooks/useEditorState';

/*
 * Types.
 */

interface RuleEditorHeaderProps {
  title?: ReactNode;
  isActive: boolean;
  isActiveReadonly?: boolean;
  isEditing: boolean;
  isReadonly: boolean;
  namespace: string;
  onDelete?: Handler;
  renderActiveTooltip?: Renderer;
  recipe?: RuleRecipe;
  breadcrumbs: ReadonlyArray<FolderBreadcrumb>;

  buttons?: ReactNode;

  onUpdateIsActive: EditorStateActions['onToggleIsActive'];
  tabs?: SettingsHeaderProps['tabs'];

  hasTable?: boolean;
  onDeleteTable?: () => void;
}

/*
 * Styles.
 */

const StyledToggleWrapper = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: 10px;
  align-items: center;
`;

const StyledActiveTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
`;

const StyledRightItemsWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-rows: min-content;
  gap: 16px;
`;

/*
 * Component.
 */

export const RuleEditorHeader: FC<RuleEditorHeaderProps> = ({
  title,
  isActive,
  isActiveReadonly = false,
  onUpdateIsActive,
  onDelete,
  renderActiveTooltip,
  namespace,
  recipe,
  isEditing,
  isReadonly,
  breadcrumbs,
  buttons,
  tabs,
  onDeleteTable,
  hasTable = false,
}) => {
  const localize = useLocalize();
  const theme = useUserTheme();

  return (
    <SettingsHeader
      tabs={tabs}
      topLine={<SettingsBreadcrumbs topLevelBreadcrumb={breadcrumbs[0]} breadcrumbs={breadcrumbs.slice(1)} />}
      right={
        <StyledRightItemsWrapper>
          {buttons}
          <StyledToggleWrapper>
            <StyledActiveTitleDiv onClick={() => !isActiveReadonly && onUpdateIsActive(!isActive)}>
              {renderIsActiveText(isActive, hasTable)}
            </StyledActiveTitleDiv>
            <TooltipCoordinator render={renderActiveTooltip} persistent>
              <Toggle
                isChecked={isActive}
                isDisabled={isReadonly || isActiveReadonly}
                size={VisualSizesEnum.SMALL}
                onChange={onUpdateIsActive}
              />
            </TooltipCoordinator>
          </StyledToggleWrapper>
          {isEditing && onDelete && (
            <DropdownBoxCoordinator
              shouldDisablePadding
              renderDropdown={(popoverProps) =>
                renderDeleteDropdown(popoverProps, onDelete, hasTable ? onDeleteTable : undefined)
              }
              renderButton={() => <IconButton label="" iconName="moreVertical" color={theme.greys.shade70} />}
            />
          )}
        </StyledRightItemsWrapper>
      }
    >
      {title ?? renderRuleEditorTitle(isEditing, namespace, isReadonly, recipe, hasTable, localize)}
    </SettingsHeader>
  );
};

/*
 * Helpers.
 */

function renderRuleEditorTitle(
  hasRule: boolean,
  namespace: string,
  isReadOnly: boolean,
  recipe: RuleRecipe | undefined,
  hasTable: boolean,
  localize: Localizer,
) {
  if (recipe && recipe.title) {
    return localize(recipe.title);
  }

  // If `hasRule` is falsy, it means we're creating a new rule.
  // Ee need to display a placeholder that depends on the namespace.
  if (!hasRule) {
    if (computeIsGlobalNamespace(namespace)) {
      return <Trans id="rule-editor-title-add-company-rule">Add a company rule</Trans>;
    }

    if (isTeamNamespace(namespace)) {
      return <Trans id="rule-editor-title-add-team-rule">Add a team rule</Trans>;
    }

    // Private namespace.
    return <Trans id="rule-editor-title-add-private-rule">Add an individual rule</Trans>;
  }

  if (isReadOnly) {
    if (hasTable) {
      return <Trans id="rule-editor-title-readonly-set">View rule set (read-only)</Trans>;
    }

    return <Trans id="rule-editor-title-readonly">View rule (read-only)</Trans>;
  }

  if (hasTable) {
    return <Trans id="rule-editor-title-edit-rule-set">Edit rule set</Trans>;
  }

  return <Trans id="rule-editor-title-edit-rule">Edit rule</Trans>;
}

function renderIsActiveText(isActive: boolean, hasTable: boolean) {
  if (hasTable) {
    if (isActive) {
      return <Trans id="rule-editor-rule-is-active-set">Rule set is active</Trans>;
    }

    return <Trans id="rule-editor-rule-is-disabled-set">Rule set is disabled</Trans>;
  }

  if (isActive) {
    return <Trans id="rule-editor-rule-is-active">Rule is active</Trans>;
  }

  return <Trans id="rule-editor-rule-is-disabled">Rule is disabled</Trans>;
}

function renderDeleteDropdown(
  popoverProps: PopoverTransferProps,
  onDelete: () => void,
  onDeleteRuleSet?: () => void,
) {
  return (
    <SimpleDropdown {...popoverProps}>
      <SimpleDropdownItem onSelect={onDelete}>
        <Trans id="rule-editor-menu-rule-delete">Delete rule</Trans>
      </SimpleDropdownItem>
      {onDeleteRuleSet && (
        <SimpleDropdownItem onSelect={onDeleteRuleSet}>
          <Trans id="rule-editor-menu-rule-set-delete">Delete rule set</Trans>
        </SimpleDropdownItem>
      )}
    </SimpleDropdown>
  );
}
